import type { AudienceContactabilityScores } from '@/interfaces/AudienceCard.interface';
import type {
  ChannelMetrics,
  PillarMetric
} from '@/interfaces/AudienceManifest.interface';

export type ContactabilityBand = 'green' | 'amber' | 'red' | 'unknown';

/** Score bands aligned to CRM-742 / Figma contactability pills. */
export function contactabilityBand(score: number | null | undefined): ContactabilityBand {
  if (score == null || Number.isNaN(score)) return 'unknown';
  if (score >= 60) return 'green';
  if (score >= 40) return 'amber';
  return 'red';
}

/**
 * Same average as the Manifest builder contactability panel
 * (data + consent + validity percentages).
 */
export function channelContactabilityPercent(
  metric: PillarMetric | null | undefined
): number | null {
  if (!metric) return null;
  const values = [
    metric.data_available_percentage,
    metric.consent_granted_percentage,
    metric.validity_confirmed_percentage
  ].filter((value) => value != null && !Number.isNaN(value));
  if (values.length === 0) return null;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

export function contactabilityScoresFromChannels(
  channels: ChannelMetrics | null | undefined
): AudienceContactabilityScores {
  if (!channels) {
    return { email: null, sms: null, whatsapp: null };
  }
  return {
    email: channelContactabilityPercent(channels.email),
    sms: channelContactabilityPercent(channels.sms),
    whatsapp: channelContactabilityPercent(channels.whatsapp)
  };
}

export function hasContactabilityScores(
  scores: AudienceContactabilityScores
): boolean {
  return scores.email != null || scores.sms != null || scores.whatsapp != null;
}

export function formatAudienceSize(size: number | null | undefined): string {
  if (size == null || Number.isNaN(size)) return '—';
  return new Intl.NumberFormat('en-GB').format(size);
}

export function formatFilterCount(count: number | null | undefined): string {
  if (count == null || Number.isNaN(count)) return '—';
  return `${count} ${count === 1 ? 'filter' : 'filters'}`;
}

export function formatCampaignCount(count: number): string {
  return `${count} ${count === 1 ? 'Campaign' : 'Campaigns'}`;
}

export function getPersonInitials(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/[\s_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}
